// @flow
import React, { Component } from "react";
import { bindActionCreators, type Dispatch } from "redux";
import { connect } from "react-redux";
import { get, noop, attempt, isEqual } from "lodash";
import axios from "axios";
import tvgConf from "@tvg/conf";
import mediator from "@tvg/mediator";
import Poller from "@tvg/poller";
import sendAppMetrics from "@tvg/metrics-collector";
import type { UnaryFn, QuinFn } from "@tvg/types/Functional";
import type {
  RaceProgram,
  Video,
  RaceVideoFeedback,
  VideoSRC
} from "@tvg/types/Race";
import VideoTpl from "@fdr/atomic-ui/_templates/RaceVideo";
import performanceUtils from "@tvg/utils/performanceUtils";
import { isMobile } from "@tvg/sh-utils/mobileUtils";
import initVideoStream from "@tvg/utils/videoStreamUtils";
import {
  showVideo,
  closeVideo,
  getStreamSrc,
  type Actions as VideoActions
} from "@fdr/shared-actions/video";
import {
  fixWagerSelector,
  fixHandicapSection,
  showRaceNavigationHeader,
  showVideoButtonHeader,
  type Actions as ProgramActions
} from "@tvg/shared-actions/ProgramActions";
import { triggerRefreshSession } from "@tvg/sh-lib-account-wallet/src/utils/mobileUtils";

const prepareVideoGTMPayload = (
  video,
  channel,
  currentRace,
  isReplay: boolean,
  method?: string
) => {
  let videoType = "live";

  if (isReplay && !channel) {
    videoType = "replay";
  }

  return {
    trackName: get(currentRace, "track.trackName", ""),
    raceNumber: get(currentRace, "raceNumber", ""),
    mtp: get(currentRace, "mtp"),
    module: "PP Header",
    videoType,
    method
  };
};

export type Props = {
  dispatch: Dispatch<ProgramActions | VideoActions>,
  getStreamSrc: QuinFn<
    string,
    string,
    boolean,
    boolean,
    ?UnaryFn<?{ message: string, code: Number }, void>,
    Promise<mixed>
  >,
  video: Video,
  isUserLogged: boolean,
  currentRace: RaceProgram,
  feedback: RaceVideoFeedback,
  stream: string,
  channel: string,
  isVideoInline: boolean,
  isSticky: boolean,
  isAutoPlay: boolean,
  videoFullscreenTilt: boolean,
  hasNewStreamVideo: boolean,
  enableHDVideo: boolean,
  trackAbbr: string,
  isURP?: boolean,
  withFixedBanner?: boolean,
  isLogged?: boolean,
  isFDTV?: boolean,
  onLiveVideoSource?: UnaryFn<VideoSRC | mixed, void>,
  videoFeedBack?: string,
  videoInteractionMethod?: string
};

type State = {
  isPlaying: boolean,
  isStreamsFetched: boolean,
  shouldStreamReplay: boolean
};
export class RaceVideoComponent extends Component<Props, State> {
  static defaultProps = {
    getStreamSrc: noop,
    isUserLogged: false,
    channel: "",
    stream: "",
    isVideoInline: false,
    isSticky: false,
    isAutoPlay: false,
    videoFullscreenTilt: false,
    hasNewStreamVideo: false,
    enableHDVideo: false,
    trackAbbr: "",
    withFixedBanner: false,
    isLogged: false,
    isFDTV: false,
    shouldAutoPlay: false,
    videoFeedBack: ""
  };

  replayFetch: IntervalID;

  refreshSessionPoller: Poller = new Poller();

  constructor(props: Props) {
    super(props);

    this.state = {
      isPlaying: false,
      isStreamsFetched: false,
      shouldStreamReplay: false,
      replayFetch: null
    };
  }

  componentDidMount() {
    const { isVideoInline } = this.props;

    if (!isVideoInline) {
      this.onWatchVideo();
    }
  }

  shouldComponentUpdate(nextProps: Props, nextState: State) {
    const mtpChanged = !isEqual(
      this.props.currentRace.mtp,
      nextProps.currentRace.mtp
    );
    const raceStatusChanged = !isEqual(
      get(this.props, "currentRace.status.code"),
      get(nextProps, "currentRace.status.code")
    );

    return (
      this.props.stream !== nextProps.stream ||
      !isEqual(this.props.video, nextProps.video) ||
      !isEqual(this.props.currentRace.id, nextProps.currentRace.id) ||
      this.state.shouldStreamReplay !== nextState.shouldStreamReplay ||
      !isEqual(this.props.currentRace.video, nextProps.currentRace.video) ||
      !isEqual(this.props.currentRace.track, nextProps.currentRace.track) ||
      mtpChanged ||
      raceStatusChanged ||
      this.props.channel !== nextProps.channel ||
      this.props.feedback !== nextProps.feedback ||
      this.props.isUserLogged !== nextProps.isUserLogged
    );
  }

  static getDerivedStateFromProps(props: Props, prevState: State) {
    const replayFile =
      get(props.currentRace, "video.mobileAvailable") &&
      get(props.currentRace, "video.replayFileName");

    const shouldStreamReplay = !!replayFile;

    if (prevState.shouldStreamReplay !== shouldStreamReplay) {
      return {
        ...prevState,
        shouldStreamReplay
      };
    }

    return null;
  }

  componentDidUpdate(prevProps: Props) {
    const { isVideoInline, video, currentRace, isUserLogged, channel } =
      this.props;

    // clear fetch replay poller
    if (video.src.replay) {
      this.clearFetchReplay();
    }

    // state for tablet and not inline for mobile
    const isPlaying = this.state.isPlaying || (!isVideoInline && video.show);

    if (
      !isUserLogged ||
      ([
        "NOT_AVAILABLE_NO_REPLAY",
        "STARTING_SOON_NO_REPLAY",
        "FINISHED_NO_REPLAY"
      ].indexOf(this.props.feedback) > -1 &&
        this.props.trackAbbr === get(currentRace, "track.trackCode"))
    ) {
      this.onCloseVideo(false);
      return;
    }

    if (
      !channel &&
      prevProps.currentRace.id !== currentRace.id &&
      this.props.hasNewStreamVideo
    ) {
      this.onCloseVideo(false);
      this.onWatchVideo();
      return;
    }

    if (prevProps.channel !== channel) {
      this.reloadStreams();
    }

    if (isVideoInline && video.show && !this.state.isStreamsFetched) {
      this.onWatchVideo();
    }

    if (isPlaying && this.shouldFetchReplay(prevProps)) {
      this.fetchReplay();
      this.clearFetchReplay();
      this.replayFetch = setInterval(this.fetchReplay, 30000);
    } else if (!channel && prevProps.currentRace.id !== currentRace.id) {
      this.reloadStreams();
    }
  }

  componentWillUnmount() {
    this.deviceLockRotationToggle();
    console.log("unmounting and closing video as well");
    this.onCloseVideo(false);
    this.clearFetchReplay();
    this.refreshSession({ stop: true });
  }

  refreshSession(options?: { stop: boolean }) {
    const timerSeconds = 60;
    if (!options?.stop && this.props.isUserLogged) {
      this.refreshSessionPoller.start(
        triggerRefreshSession,
        timerSeconds * 1000
      );
    } else {
      this.refreshSessionPoller.stop();
    }
  }

  // this is a hack to have live stream and the replay open at the same time.
  // we change the source from props to state for a better control
  onLiveSourceResponse = (src: VideoSRC | mixed) => {
    const { isFDTV, channel, onLiveVideoSource } = this.props;
    if (src && isFDTV && channel && typeof onLiveVideoSource === "function") {
      onLiveVideoSource(src);
    }
  };

  onWatchVideo = () => {
    const { dispatch, currentRace, channel } = this.props;
    const isMobileVideoAvailable = !!get(currentRace, "video.mobileAvailable");

    const stream = !channel
      ? get(currentRace, "video.streams[0]", "")
      : channel;

    const replayFile = isMobileVideoAvailable
      ? get(currentRace, "video.replayFileName", null) || ""
      : "";

    const isStreamHighDefinition =
      this.props.enableHDVideo &&
      !!get(currentRace, "video.isStreamHighDefinition");

    this.setState({
      isStreamsFetched: true
    });

    initVideoStream(
      stream,
      replayFile,
      !!channel,
      true,
      isStreamHighDefinition,
      this.props.getStreamSrc,
      this.onVideoError
    ).then(this.onLiveSourceResponse);

    this.openVideoOffset();
    dispatch(showRaceNavigationHeader(false));
    performanceUtils.register("video_start");

    mediator.base.dispatch({
      type: "VIDEO:OPEN",
      payload: {
        module: "PP Header",
        videoType: this.state.shouldStreamReplay ? "Replay" : "Live",
        trackName: get(this.props, "currentRace.track.trackName"),
        raceNumber: get(this.props, "currentRace.raceNumber"),
        videoProvider: "RCN",
        method: this.props.videoInteractionMethod,
        mtp: get(this.props, "currentRace.mtp")
      }
    });

    this.refreshSession();
  };

  onVideoStart = () => {
    const { channel, currentRace } = this.props;
    const track = channel || get(currentRace, "track.trackCode");

    performanceUtils.measure("video_start", { track, error: "false" });
  };

  onCloseVideo = (closedManually: boolean = true) => {
    const { dispatch } = this.props;
    if (closedManually) {
      // gtm event - open race selector
      mediator.base.dispatch({
        type: "VIDEO:CLOSE",
        payload: {
          module: "PP Header",
          videoType: this.state.shouldStreamReplay ? "Replay" : "Live",
          trackName: get(this.props, "currentRace.track.trackName"),
          raceNumber: get(this.props, "currentRace.raceNumber"),
          videoProvider: "RCN"
        }
      });
    }

    dispatch(closeVideo());
    dispatch(showRaceNavigationHeader(false));
    dispatch(showVideoButtonHeader(false));
    dispatch(fixWagerSelector({ isWagerSelectorFixed: false, fixedOffset: 0 }));

    this.setState({
      isPlaying: false,
      isStreamsFetched: false,
      shouldStreamReplay: false
    });

    if (typeof window !== "undefined") {
      window.scroll({
        top: 0,
        left: 0,
        behavior: "smooth"
      });
    }

    this.deviceLockRotationToggle();
    this.refreshSession({ stop: true });
  };

  onVideoMute = (muted: boolean = true) => {
    const { video, channel, currentRace } = this.props;
    const { shouldStreamReplay } = this.state;
    const payload = {
      ...prepareVideoGTMPayload(
        video,
        channel,
        currentRace,
        shouldStreamReplay
      ),
      muted
    };

    mediator.base.dispatch({
      type: "VIDEO:MUTE",
      payload
    });
  };

  onVideoFullScreen = (fullScreen: boolean = false, tilt: boolean = false) => {
    const { video, channel, currentRace } = this.props;
    const { shouldStreamReplay } = this.state;
    const payload = {
      ...prepareVideoGTMPayload(
        video,
        channel,
        currentRace,
        shouldStreamReplay
      ),
      fullScreen,
      tilt
    };

    mediator.base.dispatch({
      type: "VIDEO:FULL_SCREEN",
      payload
    });
  };

  onVideoPlay = () => {
    const { video, channel, currentRace } = this.props;
    const { shouldStreamReplay } = this.state;
    const payload = prepareVideoGTMPayload(
      video,
      channel,
      currentRace,
      shouldStreamReplay
    );

    mediator.base.dispatch({
      type: "VIDEO:PLAY_VIDEO",
      payload
    });

    this.deviceLockRotationToggle(false);
    this.refreshSession();
  };

  onVideoPause = () => {
    const { video, channel, currentRace } = this.props;
    const { shouldStreamReplay } = this.state;

    const payload = prepareVideoGTMPayload(
      video,
      channel,
      currentRace,
      shouldStreamReplay
    );

    mediator.base.dispatch({
      type: "VIDEO:STOP_VIDEO",
      payload
    });

    this.refreshSession({ stop: true });
  };

  onVideoSuccess = () => {
    const { video, channel, currentRace } = this.props;
    const { shouldStreamReplay } = this.state;
    const payload = prepareVideoGTMPayload(
      video,
      channel,
      currentRace,
      shouldStreamReplay,
      this.props.videoInteractionMethod
    );

    mediator.base.dispatch({
      type: "VIDEO:SUCCESS",
      payload
    });
  };

  onNavigateVideo = (forward: boolean = true) => {
    const { video, channel, currentRace } = this.props;
    const { shouldStreamReplay } = this.state;
    const payload = {
      ...prepareVideoGTMPayload(
        video,
        channel,
        currentRace,
        shouldStreamReplay
      ),
      forward
    };

    mediator.base.dispatch({
      type: "VIDEO:NAVIGATE",
      payload
    });
  };

  onSwitchVideoType = (switchToLive: boolean = true) => {
    const { video, channel, currentRace } = this.props;
    const { shouldStreamReplay } = this.state;
    const payload = {
      ...prepareVideoGTMPayload(
        video,
        channel,
        currentRace,
        shouldStreamReplay
      ),
      switchToLive
    };

    mediator.base.dispatch({
      type: "VIDEO:SWITCH_STREAMS",
      payload
    });
  };

  onVideoError = (error: ?{ message: string, code: Number }) => {
    const { channel, currentRace } = this.props;
    const track = channel || get(currentRace, "track.trackCode");
    performanceUtils.measure("video_start", { track, error: "true" });
    const accountId = attempt(() => sessionStorage.getItem("userId"));
    sendAppMetrics("frontend-fdm", accountId, ["open_video_error"]);

    const errorCode = [
      "UNKNOWN_ERROR",
      "MEDIA_ERR_ABORTED",
      "MEDIA_ERR_NETWORK",
      "MEDIA_ERR_DECODE",
      "MEDIA_ERR_SRC_NOT_SUPPORTED",
      "MEDIA_ERR_ENCRYPTED"
    ];

    axios.post({
      url: "/mbl/logerror",
      withCredentials: true,
      data: {
        msg: `Error streaming video for track: ${track} with source: ${get(
          this.props,
          "video.src.sd"
        )}`,
        error: {
          message: get(error, "message", error),
          code: errorCode[get(error, "code", 0)]
        },
        accountId,
        tvgContext: tvgConf().context()
      }
    });

    this.deviceLockRotationToggle();
    this.refreshSession({ stop: true });
  };

  onVideoErrorGTM = (error: string) => {
    const { video, channel, currentRace } = this.props;
    const { shouldStreamReplay } = this.state;

    const payload = {
      ...prepareVideoGTMPayload(
        video,
        channel,
        currentRace,
        shouldStreamReplay
      ),
      error
    };

    mediator.base.dispatch({
      type: "VIDEO:ERROR",
      payload
    });
  };

  onVideoOpen = () => {
    const { dispatch, isUserLogged } = this.props;

    const setVideoOpen = () => {
      dispatch(showVideo());
      this.setState({
        isPlaying: true
      });
    };

    if (!isUserLogged) {
      mediator.base.dispatch({
        type: "OPEN_LOGIN",
        payload: {
          callback: (error, success) => {
            if (success) {
              setVideoOpen();
            }
          }
        }
      });
    } else {
      setVideoOpen();
    }

    this.refreshSession();
  };

  clearFetchReplay = () => {
    const replayInterval = this.replayFetch;
    if (replayInterval) {
      clearInterval(replayInterval);
    }
  };

  reloadStreams = () => {
    const { currentRace, channel } = this.props;
    const isMobileVideoAvailable = !!get(currentRace, "video.mobileAvailable");

    const stream = !channel
      ? get(currentRace, "video.streams[0]", "")
      : channel;

    const replayFile = isMobileVideoAvailable
      ? get(currentRace, "video.replayFileName", null) || ""
      : "";

    const isStreamHighDefinition =
      this.props.enableHDVideo &&
      !!get(currentRace, "video.isStreamHighDefinition");

    initVideoStream(
      stream,
      replayFile,
      !!channel,
      true,
      isStreamHighDefinition,
      this.props.getStreamSrc,
      this.onVideoError
    ).then(this.onLiveSourceResponse);
  };

  shouldFetchReplay = (prevProps: Props): boolean => {
    const currentRaceVideo = get(this.props.currentRace, "video");
    const previousRaceVideo = get(prevProps.currentRace, "video");
    const replayLoaded = get(this.props, "video.src.replay");
    const isMobileVideoAvailable = !!get(
      this.props.currentRace,
      "video.mobileAvailable"
    );

    return (
      !this.props.channel &&
      isMobileVideoAvailable &&
      currentRaceVideo.replayFileName !== null &&
      (currentRaceVideo.replayFileName !== previousRaceVideo.replayFileName ||
        !replayLoaded)
    );
  };

  fetchReplay = () => {
    const { currentRace } = this.props;
    const isMobileVideoAvailable = !!get(currentRace, "video.mobileAvailable");
    const isStreamHighDefinition =
      this.props.enableHDVideo &&
      !!get(currentRace, "video.isStreamHighDefinition");

    initVideoStream(
      "",
      isMobileVideoAvailable
        ? get(currentRace, "video.replayFileName", "")
        : "",
      false,
      true,
      isStreamHighDefinition,
      this.props.getStreamSrc,
      this.onVideoError
    );
  };

  deviceLockRotationToggle = (lock: boolean = true) => {
    if (this.props.videoFullscreenTilt) {
      mediator.ios.dispatch({
        type: "IPHONE_LOCK_SCREEN_ROTATION",
        payload: { lockScreenRotation: lock ? 0 : 2 }
      });
    }
  };

  currentRaceBuilder = () => {
    const { currentRace } = this.props;

    return currentRace
      ? {
          mtp: get(currentRace, "mtp"),
          raceNumber: get(currentRace, "raceNumber"),
          trackAbbr: get(currentRace, "track.trackCode"),
          trackName: get(currentRace, "track.shortName"),
          status: get(currentRace, "status.code"),
          postTime: get(currentRace, "postTime"),
          isGreyhound: get(currentRace, "type.code") === "G"
        }
      : null;
  };

  openVideoOffset() {
    if (typeof window !== "undefined") {
      const fixedOffset = (window.innerWidth * 9) / 16;
      this.props.dispatch(
        fixWagerSelector({ isWagerSelectorFixed: true, fixedOffset })
      );
      this.props.dispatch(
        fixHandicapSection({
          isHandicapFixed: false,
          fixedOffset: 0,
          handicapFormatHeight: 24
        })
      );
    }
  }

  render() {
    const {
      video,
      feedback,
      isVideoInline,
      isSticky,
      isAutoPlay,
      channel,
      currentRace,
      videoFullscreenTilt,
      hasNewStreamVideo,
      withFixedBanner,
      isURP,
      isUserLogged,
      isFDTV
    } = this.props;
    const { shouldStreamReplay } = this.state;
    const showLiveVideo = feedback && feedback.indexOf("AVAILABLE") === 0;
    const isMobileVideoAvailable = !!get(currentRace, "video.mobileAvailable");
    const videoSRC = {
      ...video.src,
      sd: showLiveVideo ? video.src.sd : null,
      hd: showLiveVideo ? video.src.hd : null,
      replay: !channel ? video.src.replay : null
    };

    const showVideoReplayMode =
      !channel && shouldStreamReplay && isMobileVideoAvailable;

    return (
      <VideoTpl
        videoSRC={videoSRC}
        isReplay={showVideoReplayMode}
        feedback={feedback}
        isVideoInline={isVideoInline}
        isVideoInlinePlaying={video.show}
        isSticky={isSticky}
        isAutoPlay={isAutoPlay}
        raceInfo={this.currentRaceBuilder()}
        onOpen={this.onVideoOpen}
        onClose={this.onCloseVideo}
        videoStartCallback={this.onVideoStart}
        onVideoSuccess={this.onVideoSuccess}
        videoErrorCallback={this.onVideoError}
        onVideoError={this.onVideoErrorGTM}
        onVideoMute={this.onVideoMute}
        onVideoFullScreen={this.onVideoFullScreen}
        onVideoPlay={this.onVideoPlay}
        onVideoPause={this.onVideoPause}
        videoFullscreenTilt={videoFullscreenTilt}
        deviceLockRotationToggle={this.deviceLockRotationToggle}
        reloadStreams={this.reloadStreams}
        onSwitchVideoType={this.onSwitchVideoType}
        onNavigateVideo={this.onNavigateVideo}
        hasNewStreamVideo={hasNewStreamVideo}
        isURP={isURP}
        withFixedBanner={withFixedBanner}
        isLogged={isUserLogged}
        isFDTV={isFDTV}
      />
    );
  }
}

export default connect(
  // Below we have a hack to be able to have two video players at the same time
  // Video prop can come from the redux store OR has a prop that the parent component passes (Ex: <RaceVideoComponent video={{sd:"blablabla"}}/>)
  // This is needed so that we can have a live channel stream video running at the same time of the replay race video.
  // To trigger this behaviour of the parent component passing the video props you need the following Props to be true:
  // isFDTV && channel && onLiveVideoSource
  // The flow is this:
  // initVideoStream Video Source Response -> onLiveVideoSource -> update parent video source state -> parent component pass video Prop with new values
  (store, ownProps) => ({
    video:
      ownProps?.isFDTV &&
      !!ownProps?.channel &&
      typeof ownProps?.onLiveVideoSource === "function"
        ? { ...store.RaceProgramVideo, src: ownProps.video }
        : store.RaceProgramVideo,
    isUserLogged: store.userData.logged,
    hasNewStreamVideo: get(
      store,
      "capi.featureToggles.hasNewStreamVideo",
      false
    ),
    enableHDVideo: get(store, "capi.featureToggles.enableHDVideo", false),
    videoInteractionMethod: get(store, "RaceProgramVideo.method", false),
    videoFullscreenTilt:
      get(store, "capi.featureToggles.videoFullscreenTilt", false) &&
      !isMobile(tvgConf().product)
  }),
  (dispatch: Dispatch<*>) => ({
    dispatch,
    // $FlowFixMe
    ...bindActionCreators({ getStreamSrc }, dispatch)
  })
)(RaceVideoComponent);
